import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UploadCloud, FileText, Download, Trash2 } from "lucide-react";
import { fmtDate } from "@/utils";
import { toast } from "sonner";

const CATEGORIES = ["general", "agreement", "receipt", "blueprint", "rera", "loan", "government_approval", "sale_deed", "legal"];

export default function Documents() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [projectId, setProjectId] = useState("");
  const [category, setCategory] = useState("general");
  const [uploading, setUploading] = useState(false);

  const { data: docs = [] } = useQuery({
    queryKey: ["documents", projectId],
    queryFn: async () => (await api.get("/documents", { params: projectId ? { project_id: projectId } : {} })).data,
  });

  async function upload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!projectId) { toast.error("Select a project first"); e.target.value = ""; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("project_id", projectId);
    fd.append("category", category);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      qc.invalidateQueries({ queryKey: ["documents"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  }

  async function remove(id) {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); qc.invalidateQueries({ queryKey: ["documents"] }); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  }

  function download(doc) {
    const token = localStorage.getItem("param_token");
    const url = `${API}/documents/${doc.id}/download?auth=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Compliance</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Document Vault</h1>
          <p className="text-sm text-zinc-500 mt-1">Agreements, RERA, receipts, blueprints — securely stored in the cloud.</p>
        </div>
        <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-56" data-testid="doc-project"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-48" data-testid="doc-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
          <label className="inline-flex items-center gap-2 px-4 h-10 bg-emerald-700 hover:bg-emerald-800 text-white rounded-md text-sm cursor-pointer transition-colors" data-testid="upload-label">
            <UploadCloud size={15} /> {uploading ? "Uploading…" : "Upload File"}
            <input type="file" hidden onChange={upload} disabled={uploading} data-testid="upload-input" />
          </label>
        </div>
      </div>

      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>File</TableHead><TableHead>Category</TableHead><TableHead>Uploaded by</TableHead>
              <TableHead>Date</TableHead><TableHead className="text-right">Size</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id} className="data-row">
                  <TableCell className="flex items-center gap-2"><FileText size={15} className="text-emerald-700" />{d.filename}</TableCell>
                  <TableCell><span className="text-[11px] uppercase tracking-widest bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded">{d.category}</span></TableCell>
                  <TableCell className="text-xs">{d.uploaded_by_name}</TableCell>
                  <TableCell className="text-xs font-mono">{fmtDate(d.uploaded_at)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{Math.round((d.size || 0) / 1024)} KB</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => download(d)} className="mr-1" data-testid={`download-${d.id}`}><Download size={13} /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(d.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-${d.id}`}><Trash2 size={13} /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {!docs.length && <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-500 py-16">No documents uploaded yet. Select a project and upload PDFs, images, blueprints or Excels.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
